import wayAllowsSeatSelection from './purchase/wayAllowsSeatSelection';

/**
 * Validates if seat selection is complete for a specific way (departure/return)
 * @param {Object} purchase - The purchase object
 * @param {string} way - The way to validate ('departs' or 'returns')
 * @returns {boolean} - Returns true if seat selection is valid/complete
 */
export const validateSeatSelectionComplete = (purchase, way) => {
  if (way === 'departs') {
    return (
      !wayAllowsSeatSelection(purchase, 'departs') ||
      Object.keys(purchase.departs.selectedSeats || {}).length > 0
    );
  }

  if (way === 'returns') {
    const hasReturn = Boolean(purchase.returns?.fragments);
    return (
      !hasReturn ||
      !wayAllowsSeatSelection(purchase, 'returns') ||
      Object.keys(purchase.returns.selectedSeats || {}).length > 0
    );
  }

  return true;
};

/**
 * Redirects to seat selection if tickets are missing and seat selection is pending
 * Used in passengers step to redirect to seats if needed
 * @param {Object} purchase - The purchase object
 * @param {Function} redirectFn - Function to handle redirection
 */
export const redirectToSeatsIfNeeded = (purchase, redirectFn) => {
  const { token, hasTickets, allowsSeatSelection } = purchase;

  // If no tickets and seat selection is allowed, check if seats are pending
  if (!hasTickets && allowsSeatSelection) {
    const pendingSeatSelection =
      !validateSeatSelectionComplete(purchase, 'departs') ||
      !validateSeatSelectionComplete(purchase, 'returns');

    if (pendingSeatSelection) {
      redirectFn(`/purchase/${token}/seats/departure`);
    }
  }
};

/**
 * Redirects to departure seats if they are not selected
 * Used in return seats step to ensure departure seats are selected first
 * @param {Object} purchase - The purchase object
 * @param {Function} redirectFn - Function to handle redirection
 */
export const redirectToDepartureSeatsIfNeeded = (purchase, redirectFn) => {
  const { token } = purchase;
  const hasValidDepartureSeats = validateSeatSelectionComplete(purchase, 'departs');

  if (!hasValidDepartureSeats) {
    redirectFn(`/purchase/${token}/seats/departure`);
  }
};

/**
 * Unified seat selection validation and redirection logic
 * Performs validation and redirects automatically if needed
 * @param {Object} purchase - The purchase object
 * @param {Object} options - Configuration options
 * @param {Function} options.redirectFn - Function to handle redirection (returnTo, history.replace, etc.)
 * @param {string} options.type - Type of validation: 'passengers' or 'returns'
 */
export const validateAndRedirectSeatSelection = (purchase, options = {}) => {
  const { redirectFn, type } = options;

  if (!purchase || !redirectFn) return;

  if (type === 'passengers') {
    redirectToSeatsIfNeeded(purchase, redirectFn);
  } else if (type === 'returns') {
    redirectToDepartureSeatsIfNeeded(purchase, redirectFn);
  }
};
